import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const inr = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

// target: {employeeId, employeeName, center ('ALL' | code), gross, net, payableDays}
export function RemoveFromPayrollDialog({ open, onClose, token, month, target, onDone }) {
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [warn, setWarn] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const shownWarn = warn || (target.payableDays > 0
    ? `${target.employeeName} has ${target.payableDays} verified payable day(s). Resolve the attendance discrepancy first, or confirm the entitlement is not owed.` : null);
  const close = () => { setReason(""); setAck(false); setWarn(null); onClose(); };
  const go = async () => {
    setBusy(true);
    try {
      await api.post("/payroll-corrections/remove", { token, month, employee_id: target.employeeId,
        center: target.center || "ALL", reason, acknowledge_attendance: ack });
      toast.success(`${target.employeeName} removed from ${target.center || "ALL"} payroll ${month}`);
      onDone?.(); close();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "PAYABLE_ATTENDANCE_EXISTS") setWarn(d.message);
      else toast.error(typeof d === "string" ? d : d?.message || "Remove failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md" data-testid="remove-payroll-dialog">
        <DialogHeader>
          <DialogTitle>Remove from Selected Payroll</DialogTitle>
          <DialogDescription>Removes this employee from the {month} payroll draft only. Employee Master is not changed.</DialogDescription>
        </DialogHeader>
        <div className="text-sm grid grid-cols-2 gap-1" data-testid="remove-payroll-details">
          <span className="text-muted-foreground">Employee</span><span>{target.employeeName}</span>
          <span className="text-muted-foreground">Employee ID</span><span>{target.employeeId}</span>
          <span className="text-muted-foreground">Centre</span><span>{target.center || "ALL"}</span>
          <span className="text-muted-foreground">Payroll month</span><span>{month}</span>
          <span className="text-muted-foreground">Gross</span><span>{inr(target.gross)}</span>
          <span className="text-muted-foreground">Net</span><span>{inr(target.net)}</span>
        </div>
        <Textarea rows={2} placeholder="Reason for removal" value={reason} onChange={(e) => setReason(e.target.value)}
          data-testid="remove-payroll-reason" />
        {shownWarn && (
          <label className="flex gap-2 text-xs text-red-700 border border-red-300 bg-red-50 rounded p-2" data-testid="remove-payroll-warning">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="remove-payroll-ack" />
            {shownWarn} I confirm this salary is not owed.
          </label>)}
        <DialogFooter>
          <Button variant="ghost" onClick={close}>Cancel</Button>
          <Button variant="destructive" onClick={go} disabled={busy || reason.trim().length < 5 || (shownWarn && !ack)}
            data-testid="remove-payroll-confirm">Remove from payroll</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
